import {
  CircularProgress, Grid, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import moment from 'moment';
import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { History } from '@mui/icons-material';
import { uploadTalentImage } from '../../../../api/talentsApi';
import { DATE_FORMAT } from '../../../../consts';
import { CheckCircleSolid } from '../../../../icons/check-circle-solid';
import { STRING_KEYS } from '../../../../language/keys';
import PerfectButton from '../../../../components/common/buttons/PerfectButton';
import { AvatarWithEdit } from '../../../../components/common/avatars';
import CopyableLabel from '../../../../components/CopyableLabel';
import { OutlineClock } from '../../../../icons/outlineClock';
import { VIEW_MODE } from '../../../../consts/enums';
import { PencilAlt } from '../../../../icons/pencil-alt';
import useNavigate from '../../../../hooks/useNavigate';
import { useAuth } from '../../../../hooks/useAuth';
import { PERMISSIONS } from '../../../../consts/permissions';
import { style } from './style';

type Props = {
  talentId: string | undefined,
  onSubmitClick: () => void,
  firstName: string,
  lastName: string,
  version: string,
  createdBy: string,
  updatedAt: string,
  updatedBy: string,
  createdAt: string,
  setProfileImageUrl: Dispatch<SetStateAction<string>>,
  setProfileImageTempUrl: Dispatch<SetStateAction<string>>,
  profileImageUrl: string,
  profileImageTempUrl: string,
  setErrorUploadImage: Dispatch<SetStateAction<{ error: boolean, errorMessage: string }>>,
  onViewHistoryClick: () => void,
  viewMode: ViewMode
};

const TalentEditHeader = ({
  talentId,
  onSubmitClick,
  firstName,
  lastName,
  version,
  createdBy,
  updatedBy,
  updatedAt,
  createdAt,
  setProfileImageUrl,
  setProfileImageTempUrl,
  profileImageUrl,
  profileImageTempUrl,
  setErrorUploadImage,
  onViewHistoryClick,
  viewMode,
}: Props): JSX.Element => {
  const { t: translate } = useTranslation();
  const { goToTalentsEdit } = useNavigate();
  const { hasPermissions } = useAuth();

  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(true);
  useEffect(() => {
    setTimeout(() => {
      setIsUploadLoading(false);
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLastChangedUsername = () => {
    if (updatedBy) {
      return updatedBy;
    }

    return createdBy;
  };

  const getLastChangedDate = () => {
    if (updatedAt) {
      return new Date(updatedAt);
    }

    return new Date(createdAt);
  };

  const getUserName = () => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    return translate(STRING_KEYS.NEW_TALENT);
  };

  const date = moment(getLastChangedDate()).format(DATE_FORMAT.TABLE_LIST);

  const lastChangedUserName = getLastChangedUsername();
  const username = getUserName();

  const handleInputUploadImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files ? e.target.files[0] : '';
    const formData = new FormData();
    formData.append('file', myFile);
    setIsUploadLoading(true);
    uploadTalentImage(formData).then((data: { fileUrl: string; }) => {
      setProfileImageUrl('');
      setProfileImageTempUrl(data.fileUrl);
    })
      .catch((error) => {
        setErrorUploadImage({
          error: true,
          errorMessage: error.message,
        });
      })
      .finally(() => {
        setIsUploadLoading(false);
      });
  }, [setIsUploadLoading, setProfileImageUrl, setErrorUploadImage, setProfileImageTempUrl]);

  const renderTalentAvatar = useCallback(
    () => {
      if (!isUploadLoading) {
        return (
          <AvatarWithEdit
            viewMode={ viewMode }
            alt={ `${firstName} ${lastName}` }
            profileImage={ profileImageUrl || profileImageTempUrl }
            handleInputUploadImage={ handleInputUploadImage }
          />
        );
      }

      return <CircularProgress />;
    },
    [
      firstName,
      lastName,
      isUploadLoading,
      profileImageUrl,
      handleInputUploadImage,
      profileImageTempUrl,
      viewMode,
    ],
  );

  const renderHistoryButton = () => {
    if (!hasPermissions([PERMISSIONS.TALENT_VIEW, PERMISSIONS.TALENT_HISTORY_LOG_VIEW])) {
      return null;
    }

    return (
      <PerfectButton
        type="icon"
        onClick={ onViewHistoryClick }
        icon={ <History />  }
      />
    );
  };

  const renderActionButtons = () => {
    if (!hasPermissions([PERMISSIONS.TALENT_EDIT])) {
      return null;
    }

    if (viewMode === VIEW_MODE.READ_ONLY) {
      return (
        <PerfectButton
          type="submit"
          bgColor="primary.main"
          title="EDIT"
          onClick={ () => {
            if (talentId) {
              goToTalentsEdit(talentId);
            }
          } }
          icon={ <PencilAlt /> }
        />
      );
    }

    if (viewMode === VIEW_MODE.EDITABLE) {
      return (
        <PerfectButton
          type="submit"
          title={ translate(STRING_KEYS.SAVE_CHANGES) }
          onClick={ onSubmitClick }
          icon={ <CheckCircleSolid viewBox="-1 -1 18 18" /> }
        />
      );
    }

    return null;
  };

  const renderHeaderButtons = () => {
    return (
      <>
        <div style={ { marginLeft: 'auto' } }>
          {renderHistoryButton()}
        </div>
        {renderActionButtons()}
      </>
    );
  };

  return (
    <Grid
      container
      justifyContent={ style.gridContainer.justifyContent }
      columns={ style.mainContainer.columns }
      mt={ style.mainContainer.mt }
      style={ style.header.style }
      sx={ style.header.sx }
    >
      <Grid item xs={ 1 } sx={ style.image } >
        {renderTalentAvatar()}
      </Grid>
      <Grid item xs={ 11 } style={ style.talentInfo.container }>
        <Box sx={ style.boxRow } >
          <Typography variant={ style.typography.variant } mb={ style.typography.marginBottom }>
            {username}
          </Typography>
          { renderHeaderButtons() }
        </Box>
        <Box sx={ { ...style.boxRow, ...style.detailsContainer } } >
          {talentId && (
            <CopyableLabel labelText={ translate(STRING_KEYS.ID, { id: talentId }) }
              dataToCopy={ talentId }
              variant="body1"
              maxWidth="18rem"
              textColor="text.primary"
            />
          )}
          {lastChangedUserName && date && (
            <>
              <Box style={ style.oulineClockWrapper }>
                <OutlineClock />
              </Box>
              <Box style={ style.lastChangedBy }>
                <Typography noWrap>
                  {translate(STRING_KEYS.LAST_CHANGED_BY,
                    {
                      date,
                      username: lastChangedUserName,
                    })}
                </Typography>
              </Box>
            </>
          )}
          {version && (
            <Box>
              <Typography noWrap>
                {translate(STRING_KEYS.VERSION, {
                  version,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid >
  );
};

export default TalentEditHeader;
